'use client'

import { LogOut, Menu, User, X } from 'lucide-react'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Logo } from '@/components/logo'
import { ThemeToggle } from '@/components/theme-toggle'
import { useAuth } from '@/contexts/auth-context'
import { getUserDisplayName } from '@/lib/jwt-auth'
import { withUtm } from '@/lib/utm'

// External URLs
const CATALOG_URL = process.env.NEXT_PUBLIC_CATALOG_URL || 'http://localhost:3003'
// Auth service URL for registration/login
const AUTH_SERVICE_URL = process.env.NEXT_PUBLIC_AUTH_URL || 'http://localhost:3005'
// Dojo — unified personal space for authenticated users
const DOJO_URL = process.env.NEXT_PUBLIC_DOJO_URL || 'https://dojo.extenshi.io'

export function Header() {
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [mounted, setMounted] = useState(false)
	const { user, signOut, isAuthenticated, loading } = useAuth()

	// Prevent hydration mismatch - only render auth UI after mount
	useEffect(() => {
		setMounted(true)
	}, [])

	// Get current URL for redirect after login
	const getCurrentUrl = () => {
		if (typeof window === 'undefined') return ''
		return window.location.href
	}

	// Show skeleton until mounted and auth state is determined
	const showSkeleton = !mounted || loading

	return (
		<header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
			<div className="container mx-auto px-4">
				<div className="flex justify-between items-center h-16">
					{/* Logo */}
					<Link href="/" className="flex items-center space-x-2">
						<Logo className="flex-shrink-0 text-primary" width={32} height={32} />
						<span className="text-xl font-bold text-foreground">extenshi.io</span>
						<span className="text-sm text-muted-foreground font-medium">/blog</span>
					</Link>

					{/* Desktop Navigation */}
					<nav className="hidden md:flex items-center space-x-6">
						<Link href="/" className="text-foreground/60 hover:text-foreground transition-colors">
							Articles
						</Link>
						<a
							href={withUtm(CATALOG_URL, { campaign: 'header-nav' })}
							className="text-foreground/60 hover:text-foreground transition-colors"
						>
							Catalog
						</a>
						<a
							href={withUtm(`${CATALOG_URL}/developers`, { campaign: 'header-nav' })}
							className="text-foreground/60 hover:text-foreground transition-colors"
						>
							For developers
						</a>
					</nav>

					{/* Auth Section */}
					<div className="hidden md:flex items-center space-x-4">
						<ThemeToggle />
						{showSkeleton ? (
							<div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-9 w-24"></div>
						) : isAuthenticated && user ? (
							<div className="flex items-center space-x-3">
								<a
									href={withUtm(DOJO_URL, { campaign: 'header-nav' })}
									className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 space-x-2"
								>
									<User size={16} />
									<span>{getUserDisplayName(user)}</span>
								</a>
								<button
									type="button"
									onClick={signOut}
									className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-9 w-9"
									title="Sign Out"
								>
									<LogOut size={16} />
								</button>
							</div>
						) : (
							<a
								href={withUtm(
									`${AUTH_SERVICE_URL}/auth/login?redirect=${encodeURIComponent(getCurrentUrl())}`,
									{ campaign: 'header-nav' },
								)}
								className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
							>
								Sign In
							</a>
						)}
					</div>

					{/* Mobile menu button */}
					<button
						type="button"
						onClick={() => setIsMenuOpen(!isMenuOpen)}
						className="md:hidden inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9"
					>
						{isMenuOpen ? <X size={20} /> : <Menu size={20} />}
					</button>
				</div>

				{/* Mobile Navigation */}
				{isMenuOpen && (
					<div className="md:hidden border-t py-4">
						<nav className="space-y-4">
							<Link
								href="/"
								className="block text-foreground/60 hover:text-foreground transition-colors"
								onClick={() => setIsMenuOpen(false)}
							>
								Articles
							</Link>
							<a
								href={withUtm(CATALOG_URL, { campaign: 'header-nav' })}
								className="block text-foreground/60 hover:text-foreground transition-colors"
								onClick={() => setIsMenuOpen(false)}
							>
								Catalog
							</a>
							<a
								href={withUtm(`${CATALOG_URL}/developers`, { campaign: 'header-nav' })}
								className="block text-foreground/60 hover:text-foreground transition-colors"
								onClick={() => setIsMenuOpen(false)}
							>
								For developers
							</a>
							<div className="flex items-center justify-between">
								<span className="text-sm text-muted-foreground">Theme</span>
								<ThemeToggle />
							</div>
							{showSkeleton ? (
								<div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded h-9 w-full"></div>
							) : isAuthenticated && user ? (
								<div className="space-y-3">
									<a
										href={withUtm(DOJO_URL, { campaign: 'header-nav' })}
										className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 w-full space-x-2"
									>
										<User size={16} />
										<span>{getUserDisplayName(user)}</span>
									</a>
									<button
										type="button"
										onClick={signOut}
										className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-secondary text-secondary-foreground hover:bg-secondary/80 h-9 px-3 w-full space-x-2"
									>
										<LogOut size={16} />
										<span>Sign Out</span>
									</button>
								</div>
							) : (
								<a
									href={withUtm(
										`${AUTH_SERVICE_URL}/auth/login?redirect=${encodeURIComponent(getCurrentUrl())}`,
										{ campaign: 'header-nav' },
									)}
									className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2 w-full text-center"
								>
									Sign In
								</a>
							)}
						</nav>
					</div>
				)}
			</div>
		</header>
	)
}
